import { Alert } from "@package/subframe/components/Alert";
import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { FeatherArrowLeft, FeatherCheck, FeatherWifiOff } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { AcknowledgementDocumentViewer } from "@/components/acknowledgements/AcknowledgementDocumentViewer";
import { ConfirmSignAcknowledgementDialog } from "@/components/acknowledgements/ConfirmSignAcknowledgementDialog";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useGetAcknowledgementRequestQueryControllerGet } from "@/generated/acknowledgements/acknowledgements";
import { useOfflineAcknowledgementSign } from "@/hooks/use-offline-acknowledgement-sign";
import { ACK_COPY } from "@/lib/acknowledgements/copy";
import { invalidateAcknowledgementQueries } from "@/lib/acknowledgements/invalidate-acknowledgement-queries";
import { itemTypeMeta } from "@/lib/acknowledgements/item-types";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import { offlineDb } from "@/lib/offline/db";

interface AcknowledgementReadConfirmPageProps {
  requestId: string;
}

/**
 * Read the document, then confirm (AC11/AC12/AC15).
 *
 * The order matters and is enforced rather than suggested: Read is recorded only
 * once the document has actually rendered, so a failed fetch can never stamp
 * "they read it" onto a record nobody can correct.
 */
export function AcknowledgementReadConfirmPage({
  requestId,
}: AcknowledgementReadConfirmPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedNurseryId } = useNursery();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cachedDetail, setCachedDetail] = useState<Record<
    string,
    unknown
  > | null>(null);
  /**
   * Whether the offline copy has been looked for yet.
   *
   * Tracked so the "could not open this" card is only ever shown once the cache
   * has actually been checked and come back empty. Without it the first frame
   * after going offline renders that card over a document that is about to load.
   */
  const [cacheLookup, setCacheLookup] = useState<"idle" | "done">("idle");
  const readMarkedRef = useRef(false);

  // "Go to the current version" is a param change on this same route, so the
  // component re-renders rather than remounting. Without this reset the cached
  // fallback below would keep serving the PREVIOUS request's document while the
  // new one loads — and confirming signs the new requestId against a document
  // the staff member never saw. The Read stamp would stick to the old one too.
  //
  // Reset during render rather than in an effect: an effect runs after commit,
  // which still paints one frame of the previous document.
  const [renderedRequestId, setRenderedRequestId] = useState(requestId);
  if (renderedRequestId !== requestId) {
    setRenderedRequestId(requestId);
    setCachedDetail(null);
    setCacheLookup("idle");
    readMarkedRef.current = false;
  }

  const detailQuery = useGetAcknowledgementRequestQueryControllerGet(
    requestId,
    { query: { refetchOnMount: "always" } },
  );

  const { sign, markRead, isPending, syncStatus } =
    useOfflineAcknowledgementSign(
      requestId,
      selectedNurseryId,
      user?.id ?? null,
    );

  const server = detailQuery.data?.data;
  // Fall back to the cached copy so the document is readable offline (AC35).
  const detail = server ?? (cachedDetail as typeof server | null);

  /**
   * Cache the payload whenever it arrives, and read it back when the network
   * cannot supply one.
   *
   * `isPaused` matters as much as `isError`, and missing it made AC35 fail in
   * exactly the case it exists for. React Query's default `networkMode` is
   * "online", so once `onlineManager` knows the tab is offline it PAUSES the
   * fetch rather than running and failing it: `fetchStatus` becomes "paused",
   * and `isError`/`isLoading` both stay false. Keying the fallback on `isError`
   * alone therefore only worked when the app was ALREADY offline as it loaded —
   * `onlineManager` starts optimistically online and only learns otherwise from
   * the window's "offline" event, so a cold start still attempts the fetch and
   * fails it honestly. The common case, a tablet carried out of WiFi range with
   * the app open, took the paused branch and rendered "reconnect and try again"
   * over a document already sitting in IndexedDB.
   */
  useEffect(() => {
    if (server) {
      void offlineDb.acknowledgementRequestDetails.put({
        id: requestId,
        cachedAt: new Date().toISOString(),
        detail: server,
      });
      return;
    }
    if (!detailQuery.isError && !detailQuery.isPaused) return;
    void offlineDb.acknowledgementRequestDetails.get(requestId).then((row) => {
      setCachedDetail(
        (row?.detail as Record<string, unknown> | undefined) ?? null,
      );
      setCacheLookup("done");
    });
  }, [server, detailQuery.isError, detailQuery.isPaused, requestId]);

  const isSigned = Boolean(detail?.signedAt);
  const isSuperseded = Boolean(detail?.supersededAt);
  const hasDocument = Boolean(detail?.content);

  /**
   * Records Read once the document is actually on screen.
   *
   * Ref-guarded and fire-and-forget: this is not a user action, so a failure is
   * never toasted — a message about it would be inexplicable. CD-11 defines Read
   * as "opened the document", which is why it keys off the document being
   * present rather than off route entry.
   */
  useEffect(() => {
    if (readMarkedRef.current) return;
    if (!hasDocument || isSigned || isSuperseded) return;
    if (detail?.readAt) {
      readMarkedRef.current = true;
      return;
    }
    readMarkedRef.current = true;
    void markRead().catch((error) => {
      console.warn("[Acknowledgement] could not record read", error);
    });
  }, [hasDocument, isSigned, isSuperseded, detail?.readAt, markRead]);

  async function handleConfirm() {
    try {
      const outcome = await sign();
      setConfirmOpen(false);

      if (outcome === "failed") {
        toast.error(ACK_COPY.TOAST_SIGN_ERROR);
        return;
      }
      toast.success(
        outcome === "queued"
          ? ACK_COPY.TOAST_SIGN_QUEUED
          : ACK_COPY.TOAST_SIGNED,
      );
      await invalidateAcknowledgementQueries(queryClient);
      void navigate({ to: "/checklists" });
    } catch (error) {
      setConfirmOpen(false);
      toast.error(extractApiErrorMessage(error, ACK_COPY.TOAST_SIGN_ERROR));
    }
  }

  // A paused query is neither loading nor errored, so `isLoading` alone would
  // fall straight through to the error card while the offline copy is still
  // being read out of IndexedDB.
  const awaitingCachedCopy =
    (detailQuery.isError || detailQuery.isPaused) && cacheLookup === "idle";

  if ((detailQuery.isLoading || awaitingCachedCopy) && !detail) {
    return (
      <div className="flex w-full items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex w-full flex-col gap-4 px-4 py-6">
        <BackLink />
        <Alert
          variant="error"
          title="Could not open this document"
          description="Reconnect and try again. Nothing has been recorded."
        />
      </div>
    );
  }

  const meta = itemTypeMeta(detail.itemType);

  return (
    <div
      className="flex w-full flex-col gap-6 px-4 py-6 mobile:px-4"
      data-testid="ack-reader"
    >
      <div className="flex w-full flex-col gap-2">
        <BackLink />
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="text-metrics font-metrics text-primary-navy-800">
            {ACK_COPY.READER_TITLE}
          </span>
          {!syncStatus.isOnline && (
            <Badge variant="neutral" icon={<FeatherWifiOff />}>
              Offline
            </Badge>
          )}
        </div>
        <span className="text-body text-subtext-color">
          {meta.singular} · version {detail.version}
        </span>
      </div>

      {isSuperseded && (
        <Alert
          variant="neutral"
          title="You don't need to sign this one"
          description={ACK_COPY.READER_SUPERSEDED}
          actions={
            detail.supersededByRequestId ? (
              <Link
                to="/acknowledgements/$requestId"
                params={{ requestId: detail.supersededByRequestId }}
              >
                <Button variant="neutral-secondary">
                  {ACK_COPY.READER_SUPERSEDED_LINK}
                </Button>
              </Link>
            ) : undefined
          }
        />
      )}

      <AcknowledgementDocumentViewer
        kind={detail.content?.kind}
        payload={detail.content?.payload as Record<string, unknown> | undefined}
      />

      {isSigned ? (
        <div
          className="flex w-full items-center gap-2 rounded-md border border-success-200 bg-success-50 px-4 py-3"
          data-testid="ack-signed-confirmation"
        >
          <FeatherCheck className="text-success-700" />
          <span className="text-body font-body text-success-700">
            {ACK_COPY.READER_ALREADY_SIGNED}
            {detail.signedAt
              ? ` on ${format(new Date(detail.signedAt), "d MMM yyyy, HH:mm")}`
              : ""}
          </span>
        </div>
      ) : (
        !isSuperseded &&
        detail.canSign && (
          <div className="flex w-full items-center justify-end">
            <Button
              variant="brand-primary"
              size="large"
              onClick={() => setConfirmOpen(true)}
              disabled={isPending}
              data-testid="ack-open-confirm"
            >
              {ACK_COPY.CONFIRM_ACTION}
            </Button>
          </div>
        )
      )}

      <ConfirmSignAcknowledgementDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        itemTitle={detail.title}
        itemTypeLabel={meta.singular}
        version={detail.version}
        dueAt={detail.dueAt}
        isSaving={isPending}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/checklists"
      className="flex items-center gap-1 text-caption font-caption text-subtext-color hover:text-default-font"
    >
      <FeatherArrowLeft />
      Back to To do
    </Link>
  );
}
